// The client's picture of the room (architecture §9.1, protocol §5.4, §8): entities with sample
// histories, players, own cell ids and score, the observer camera, and the latest HUD feeds.
// `sample(rt)` turns it into this display frame's draw lists.
//
// Implementer: client net/game. Consumers: net/handlers.ts (writes), render (reads `view`,
// `players`, `info`, `own`), game/camera.ts (own cells, observer camera), ui/hud (feeds), app.ts,
// the server's view-diff property test (architecture §10.1) — so this module is DOM-free.
//
// Apply order inside one FRAME (protocol §5.4): PLAYERS, ADD, MOVE, EATEN, REMOVE, then OWN,
// CAMERA and the HUD sections.
//
// Timing (protocol §8.3): every change is stamped with its FRAME tick and shown against the
// delayed render tick `rt`: an ADD at T is drawn once rt ≥ T, an EATEN/REMOVE at T takes effect
// once rt ≥ T. Records therefore outlive their removal for a moment (the eaten slide lasts
// 100 ms after that) and are dropped by `sample()`; removals older than STALE_REMOVAL_MS are also
// swept by `applyFrame`, so a hidden tab (no `sample()` calls) does not accumulate them.
//
// Own cells in `view.own` are the cells of the current life (SPAWNED playerId) — and, after
// DEATH, of the life that just ended until its last cell has left the display — so the camera
// follows exactly what is drawn, including the final "being eaten" slide.
//
// Allocation: `sample()` allocates nothing (dense record list, a persistent body order sorted
// with an insertion sort, reused output arrays and pooled eat events). `applyFrame()` reuses
// pooled records.
//
// Eat events (backlog C-4, audio §3): `sample(rt)` lists in `view.eatEvents` every EATEN record
// whose removal took effect in that call (rt ≥ removedTick for the first time: the moment its
// slide starts on screen), once each. `events/render-tap.ts` turns them into client-bus events.
//
// Money rooms (money-mode §4, §7; protocol §5.2–5.4): `money` holds the WELCOME money block, the
// label price, every known player's value (FRAME MONEY), every running cash-out hold
// (CASH_OUT_STATE, animated locally from `cashOutHoldSec`), and the last DEATH valueLost /
// CASH_OUT_DONE. The client never computes a value: it only stores and formats what the server
// sends (`formatMoney`).

import {
  type AddRecord,
  AMOUNT_KIND,
  type AmountKind,
  type CameraSection,
  type CashOutDoneMessage,
  type CashOutStateMessage,
  type DeathMessage,
  type FrameMessage,
  INTERPOLATION,
  KIND,
  type Kind,
  type LabelCurrency,
  lamportsToSol,
  lamportsToUsd,
  type LeaderboardSection,
  massOf,
  type MatchSection,
  type MinimapSection,
  type MoneySection,
  MOVE_MASK,
  type MoveRecord,
  type OwnSection,
  pelletColor,
  type PlayerRecord,
  type RoomSection,
  SPECIAL_COLORS,
  type TeamsSection,
  type WelcomeMessage,
  type WelcomeMoneyInfo,
  DEFAULT_POS_Q,
} from '@ballio/shared';
import { eatenProgress, type Point3, popInProgress, SampleHistory } from './interpolate.js';

export const ENTITY_EFFECT = { NONE: 0, POP_IN: 1, EATEN: 2 } as const;
export type EntityEffect = (typeof ENTITY_EFFECT)[keyof typeof ENTITY_EFFECT];

/** One known entity. Records are reused; never keep a reference across a `clear()`. */
export interface EntityRecord {
  readonly id: number;
  readonly kind: Kind;
  /** Owner player id (cells); 0 otherwise. */
  readonly playerId: number;
  /**
   * Base colour (0xRRGGBB): ejected → its ADD colour; cells → the owner's PLAYERS colour (kept
   * current); food → `pelletColor(id)` from the default palette; virus/spawner →
   * SPECIAL_COLORS. The renderer may override (plain colours, theme palette).
   */
  color: number;
  readonly hist: SampleHistory;
  /** ADD tick: drawn once rt ≥ addedTick. */
  addedTick: number;
  /** EATEN/REMOVE tick, or −1 while known. Takes effect once rt ≥ removedTick. */
  removedTick: number;
  /** The eater (EATEN), or 0 (REMOVE). */
  eaterId: number;

  // --- written by sample(rt) ---
  /** Display position and radius (effects applied). */
  x: number;
  y: number;
  r: number;
  /** In this frame's draw lists. */
  visible: boolean;
  effect: EntityEffect;
  /** Effect progress 0–1. */
  effectT: number;
}

/** The draw lists of one display frame. Arrays are reused frame to frame. */
export interface SampledView {
  /** The render tick sampled. */
  rt: number;
  /** Visible food (draw first). */
  readonly food: EntityRecord[];
  /** Visible ejected mass. */
  readonly ejected: EntityRecord[];
  /** Visible cells, viruses and spawners sorted by r ascending, ties by id (architecture §9.2). */
  readonly bodies: EntityRecord[];
  /** Visible own cells (a subset of `bodies`), for the camera. */
  readonly own: EntityRecord[];
  /**
   * EATEN records whose removal took effect in this `sample()` call (each record once), in no
   * particular order. Pooled: valid until the next `sample()`; copy what you keep.
   */
  readonly eatEvents: EatEvent[];
}

/**
 * One eat record at the render tick it takes effect (backlog C-4, audio §3 "eat record"). "Own"
 * means a cell of the current life (`own.playerId`) or of the life that just ended and is still
 * on screen (so the last own cell's eat after DEATH still counts as own).
 */
export interface EatEvent {
  readonly eatenId: number;
  readonly eatenKind: Kind;
  /** Owner of the eaten cell; 0 for non-cells. */
  readonly eatenPlayerId: number;
  readonly eaterId: number;
  /** The eater's kind, or null when the eater is unknown to this client. */
  readonly eaterKind: Kind | null;
  /** Owner of the eater cell; 0 when not a cell or unknown. */
  readonly eaterPlayerId: number;
  /** The eaten entity is an own cell. */
  readonly eatenOwn: boolean;
  /** The eater is an own cell. */
  readonly eaterOwn: boolean;
  /**
   * Own cells left after this removal (cells not yet removed at this render tick). Within one
   * `sample()` the own-cell removals count down, so when two own cells go at once only the last
   * one reports 0 (audio §3 row 6: `death` replaces `eat` for the last own cell only).
   */
  readonly ownLeft: number;
  /** The FRAME tick of the EATEN. */
  readonly tick: number;
}

/** The newest-FRAME summary of the own cells (movement.md §4.2–4.3 anchor). */
export interface OwnNewest {
  /** r²-weighted centroid of the own cells' newest samples. */
  x: number;
  y: number;
  /** r²-weighted velocity (u/s) from each cell's two newest samples; 0 with one sample. */
  vx: number;
  vy: number;
  /** Radius of the largest own cell. */
  rMax: number;
  /** Mass of the largest own cell (`massOf(rMax)`). */
  massMax: number;
  /** Own cells counted (live, with a sample). */
  count: number;
  /** Newest sample tick among them. */
  tick: number;
}

/** A player's cash-out hold (CASH_OUT_STATE), as last received. */
export interface CashOutHold {
  readonly playerId: number;
  /** Server progress 0–1 at `atMs`. */
  progress: number;
  /** Local time (`performance.now()` ms) the state arrived; the ring animates from there. */
  atMs: number;
  /** Local time the hold was first seen active. */
  startedAtMs: number;
}

/** Money-room state (money rooms only; all empty in free rooms). */
export interface MoneyState {
  /** WELCOME money block; null in free rooms (and before WELCOME). */
  room: WelcomeMoneyInfo | null;
  /** Label price, USD per SOL; 0 = unknown. WELCOME snapshot, then FRAME MONEY (0 = keep). */
  solUsd: number;
  /** Player id → value in integer lamports (FRAME MONEY; merged, never computed): the bags. */
  readonly values: Map<number, number>;
  /** Player id → running cash-out hold (active ones only; a stop deletes the entry). */
  readonly holds: Map<number, CashOutHold>;
  /** DEATH valueLost of the last own death in a money room; null otherwise. */
  lastValueLost: number | null;
  /** The last CASH_OUT_DONE (cleared on SPAWNED and WELCOME). */
  lastCashOut: CashOutDoneMessage | null;
  /** Bumps on every change of `solUsd`, `values` or `holds` (cheap change detection). */
  version: number;
}

/** Own life state (SPAWNED + OWN). */
export interface OwnState {
  /** This life's player id from SPAWNED; 0 when not playing. */
  playerId: number;
  /** OWN cell ids (the full list from the last OWN). */
  readonly cellIds: Set<number>;
  /** OWN score (floor Σ mass). */
  score: number;
  /** True from the first OWN after SPAWNED until DEATH. */
  alive: boolean;
}

/** The latest value of a periodic HUD section, with the tick it arrived on (0 = never). */
export interface HudFeed<T> {
  value: T | null;
  tick: number;
  /** Increments on every update (cheap change detection for the HUD). */
  version: number;
}

/** Removed records still in the store after this long (in FRAME time) are dropped by applyFrame. */
export const STALE_REMOVAL_MS = 2000;

/** Above this many PLAYERS records, the least recently seen ones without cells are forgotten. */
export const MAX_PLAYER_RECORDS = 4096;

/** Pooled entity records kept for reuse. */
const MAX_POOLED_RECORDS = 8192;

/** Records dropped outside `sample()` that trigger an early reclaim. */
const RECLAIM_BATCH = 512;

function feed<T>(): HudFeed<T> {
  return { value: null, tick: 0, version: 0 };
}

function setFeed<T>(f: HudFeed<T>, value: T, tick: number): void {
  f.value = value;
  f.tick = tick;
  f.version++;
}

/** Shrinks a reused list to `n` (a no-op when unchanged, so steady frames never touch it). */
function setLength(list: EntityRecord[], n: number): void {
  if (list.length !== n) list.length = n;
}

function isBodyKind(kind: Kind): boolean {
  return kind === KIND.CELL || kind === KIND.VIRUS || kind === KIND.SPAWNER;
}

/** A cell of the current life (`ownId`) or of the life still on screen after it ended. */
function isOwnCell(rec: EntityRecord, ownId: number, dyingId: number): boolean {
  return (
    rec.kind === KIND.CELL &&
    rec.playerId !== 0 &&
    (rec.playerId === ownId || rec.playerId === dyingId)
  );
}

/** Draw order of bodies: r ascending, ties by id (architecture §9.2). */
function drawsAfter(a: Rec, b: Rec): boolean {
  return a.r > b.r || (a.r === b.r && a.id > b.id);
}

/** The store's record type: the public fields plus bookkeeping. */
class Rec implements EntityRecord {
  id = 0;
  kind: Kind = KIND.FOOD;
  playerId = 0;
  color = 0;
  readonly hist = new SampleHistory();
  addedTick = 0;
  removedTick = -1;
  eaterId = 0;
  x = 0;
  y = 0;
  r = 0;
  visible = false;
  effect: EntityEffect = ENTITY_EFFECT.NONE;
  effectT = 0;
  /** Index in the dense list; −1 = not in the store (dropped or pooled). */
  slot = -1;
  /** A cell ADDed before its PLAYERS record (colour still the fallback). */
  orphan = false;
  /** Its EATEN removal has been reported in `view.eatEvents`. */
  eatReported = false;
}

/** The pooled, writable form of EatEvent. */
type EatEventRec = { -readonly [K in keyof EatEvent]: EatEvent[K] };

function newEatEvent(): EatEventRec {
  return {
    eatenId: 0,
    eatenKind: KIND.FOOD,
    eatenPlayerId: 0,
    eaterId: 0,
    eaterKind: null,
    eaterPlayerId: 0,
    eatenOwn: false,
    eaterOwn: false,
    ownLeft: 0,
    tick: 0,
  };
}

function newMoneyState(): MoneyState {
  return {
    room: null,
    solUsd: 0,
    values: new Map(),
    holds: new Map(),
    lastValueLost: null,
    lastCashOut: null,
    version: 0,
  };
}

export class WorldStore {
  /** The WELCOME of the current socket (bounds, quant, tickRate, virus constants, flags); null before. */
  info: WelcomeMessage | null = null;
  readonly entities = new Map<number, EntityRecord>();
  /** PLAYERS records by player id. */
  readonly players = new Map<number, PlayerRecord>();
  readonly own: OwnState = { playerId: 0, cellIds: new Set(), score: 0, alive: false };
  /** Last CAMERA section (observers); null when not sent yet. */
  observerCamera: CameraSection | null = null;
  readonly leaderboard: HudFeed<LeaderboardSection> = feed();
  readonly teams: HudFeed<TeamsSection> = feed();
  readonly minimap: HudFeed<MinimapSection> = feed();
  readonly match: HudFeed<MatchSection> = feed();
  readonly room: HudFeed<RoomSection> = feed();
  /** Money-room state (values, price, holds, last valueLost / cash-out). */
  readonly money: MoneyState = newMoneyState();
  /** Tick of the last applied FRAME (−1 before the first). */
  lastTick = -1;
  /** The last `sample()` result. */
  readonly view: SampledView = {
    rt: 0,
    food: [],
    ejected: [],
    bodies: [],
    own: [],
    eatEvents: [],
  };

  /** Every record in the store (live or still showing its removal), in no particular order. */
  private readonly list: Rec[] = [];
  /** Body records (cells, viruses, spawners), kept in draw order between frames. */
  private readonly order: Rec[] = [];
  /** Dropped records waiting for `order` to be compacted before they may be reused. */
  private readonly freed: Rec[] = [];
  private readonly pool: Rec[] = [];
  /** Removal queue (FIFO by tick) for the stale sweep: record, its id and removal tick. */
  private readonly pendRec: Rec[] = [];
  private readonly pendId: number[] = [];
  private readonly pendTick: number[] = [];
  private pendHead = 0;
  /** The life that just ended, while its cells are still on screen. */
  private dyingPlayerId = 0;
  /** Cells whose PLAYERS record had not arrived at ADD time. */
  private orphanCells = 0;
  private dtMs = 1000 / 25;
  private posQ = DEFAULT_POS_Q;
  private readonly tmp: Point3 = { x: 0, y: 0, r: 0 };
  /** Pooled eat events (`view.eatEvents[i]` is `eatPool[i]`). */
  private readonly eatPool: EatEventRec[] = [];
  private readonly ownNewestOut: OwnNewest = {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    rMax: 0,
    massMax: 0,
    count: 0,
    tick: 0,
  };

  /** ms per server tick (from WELCOME; 40 before). */
  get tickMs(): number {
    return this.dtMs;
  }

  /** Records currently held (live plus those still showing their removal). */
  get size(): number {
    return this.list.length;
  }

  /** New socket: forget everything, keep `w` as `info`. */
  welcome(w: WelcomeMessage): void {
    this.info = w;
    this.dtMs = w.tickRate > 0 && Number.isFinite(w.tickRate) ? 1000 / w.tickRate : 1000 / 25;
    this.posQ = w.posQ > 0 && Number.isFinite(w.posQ) ? w.posQ : DEFAULT_POS_Q;
    this.clear();
    this.own.playerId = 0;
    this.own.score = 0;
    this.own.alive = false;
    for (const f of [this.leaderboard, this.teams, this.minimap, this.match, this.room]) {
      f.value = null;
      f.tick = 0;
      f.version++;
    }
    const m = this.money;
    m.room = w.money ?? null;
    m.solUsd =
      w.money && w.money.solUsd > 0 && Number.isFinite(w.money.solUsd) ? w.money.solUsd : 0;
    m.values.clear();
    m.holds.clear();
    m.lastValueLost = null;
    m.lastCashOut = null;
    m.version++;
    this.lastTick = -1;
  }

  /** NOTICE feedResumed (mechanics §15) or a room change: forget entities, players, own ids and camera; keep `info`. */
  clear(): void {
    for (const rec of this.list) {
      rec.slot = -1;
      this.release(rec);
    }
    for (const rec of this.freed) this.release(rec);
    this.list.length = 0;
    this.order.length = 0;
    this.freed.length = 0;
    this.entities.clear();
    this.players.clear();
    this.pendRec.length = 0;
    this.pendId.length = 0;
    this.pendTick.length = 0;
    this.pendHead = 0;
    this.orphanCells = 0;
    this.dyingPlayerId = 0;
    this.own.cellIds.clear();
    this.observerCamera = null;
    const v = this.view;
    v.food.length = 0;
    v.ejected.length = 0;
    v.bodies.length = 0;
    v.own.length = 0;
    v.eatEvents.length = 0;
  }

  /** Applies one decoded FRAME (sections in protocol order). */
  applyFrame(frame: FrameMessage): void {
    const T = frame.tick;
    this.lastTick = T;
    if (frame.players) this.applyPlayers(frame.players);
    if (frame.add) for (const a of frame.add) this.applyAdd(a, T);
    if (frame.move) for (const m of frame.move) this.applyMove(m, T);
    if (frame.eaten) for (const e of frame.eaten) this.markRemoved(e.eatenId, T, e.eaterId);
    if (frame.remove) for (const id of frame.remove) this.markRemoved(id, T, 0);
    if (frame.own) this.applyOwn(frame.own);
    if (frame.camera) this.observerCamera = frame.camera;
    if (frame.leaderboard) setFeed(this.leaderboard, frame.leaderboard, T);
    if (frame.teams) setFeed(this.teams, frame.teams, T);
    if (frame.minimap) setFeed(this.minimap, frame.minimap, T);
    if (frame.match) setFeed(this.match, frame.match, T);
    if (frame.room) setFeed(this.room, frame.room, T);
    if (frame.money) this.applyMoney(frame.money);
    this.sweepStale(T);
    if (this.freed.length >= RECLAIM_BATCH) this.reclaim();
  }

  /** SPAWNED: the new life's id (alive becomes true with the next OWN). */
  spawned(playerId: number): void {
    this.own.playerId = playerId;
    this.own.cellIds.clear();
    this.own.score = 0;
    this.own.alive = false;
    this.dyingPlayerId = 0;
    this.money.lastValueLost = null;
    this.money.lastCashOut = null;
  }

  /**
   * DEATH: own state cleared (playerId 0, no cells, alive false). `msg` (optional) records the
   * money-room `valueLost` in `money.lastValueLost`.
   */
  died(msg?: DeathMessage): void {
    this.endOwnLife();
    this.money.lastValueLost = msg?.valueLost ?? null;
  }

  /**
   * CASH_OUT_DONE (money-mode §4): the life ended by a cash-out (the cells are REMOVEd by the
   * server); own state is cleared like a death and the message kept in `money.lastCashOut`.
   */
  cashedOut(msg: CashOutDoneMessage): void {
    this.endOwnLife();
    this.money.lastValueLost = null;
    this.money.lastCashOut = msg;
  }

  /**
   * CASH_OUT_STATE (any player): an active state starts or refreshes that player's hold, an
   * inactive one ends it. `atMs` = local arrival time. Returns the hold before this message
   * (null when none was running), so callers can tell a start from a refresh.
   */
  applyCashOutState(msg: CashOutStateMessage, atMs: number): CashOutHold | null {
    const holds = this.money.holds;
    const prev = holds.get(msg.playerId) ?? null;
    const prevCopy = prev ? { ...prev } : null;
    const progress = Number.isFinite(msg.progress) ? Math.min(1, Math.max(0, msg.progress)) : 0;
    if (msg.active) {
      if (prev) {
        prev.progress = progress;
        prev.atMs = atMs;
      } else {
        holds.set(msg.playerId, {
          playerId: msg.playerId,
          progress,
          atMs,
          startedAtMs: atMs,
        });
      }
    } else {
      holds.delete(msg.playerId);
    }
    this.money.version++;
    return prevCopy;
  }

  /**
   * Interpolates every entity at render tick `rt` (protocol §8.3), applies effects (eaten
   * slide-to-eater with radius → 0 over 100 ms; food pop-in 150 ms), drops records whose removal
   * effect ended, fills and returns `this.view`. No allocation per call.
   */
  sample(rt: number): SampledView {
    const v = this.view;
    v.rt = rt;
    // The output lists are filled by index and their length is set once at the end: `length = 0`
    // would drop V8's backing store and make every frame reallocate it.
    const food = v.food;
    const ejected = v.ejected;
    let nFood = 0;
    let nEjected = 0;
    const dtMs = this.dtMs;
    const dyingId = this.dyingPlayerId;
    const liveOwnId = this.own.playerId;
    let dyingSeen = false;
    let nEat = 0;
    let ownLive = 0;
    const list = this.list;
    let i = 0;
    while (i < list.length) {
      const rec = list[i] as Rec;
      if (rec.removedTick >= 0 && rt >= rec.removedTick) {
        // The removal is in effect: REMOVE vanishes at once, EATEN slides into its eater.
        if (rec.eaterId !== 0 && !rec.eatReported) {
          rec.eatReported = true;
          this.recordEat(rec, nEat++, liveOwnId, dyingId);
        }
        if (rec.eaterId === 0 || !this.sampleEaten(rec, rt, dtMs)) {
          this.drop(rec);
          continue; // the last record moved into slot i
        }
      } else if (rt < rec.addedTick) {
        // Not on screen yet; keep r meaningful for the body order.
        const n = rec.hist.newest();
        if (n) rec.r = n.r;
        rec.visible = false;
        rec.effect = ENTITY_EFFECT.NONE;
        rec.effectT = 0;
      } else {
        rec.hist.at(rt, rec);
        rec.visible = true;
        rec.effect = ENTITY_EFFECT.NONE;
        rec.effectT = 0;
        if (rec.kind === KIND.FOOD) {
          const t = popInProgress(rt, rec.addedTick, dtMs, INTERPOLATION.foodPopInMs);
          if (t < 1) {
            rec.r *= t;
            rec.effect = ENTITY_EFFECT.POP_IN;
            rec.effectT = t;
          }
        }
      }
      if (dyingId !== 0 && rec.playerId === dyingId) dyingSeen = true;
      if (
        rec.kind === KIND.CELL &&
        rec.playerId !== 0 &&
        (rec.playerId === liveOwnId || rec.playerId === dyingId) &&
        !(rec.removedTick >= 0 && rt >= rec.removedTick)
      ) {
        ownLive++;
      }
      if (rec.visible) {
        if (rec.kind === KIND.FOOD) food[nFood++] = rec;
        else if (rec.kind === KIND.EJECTED) ejected[nEjected++] = rec;
      }
      i++;
    }
    setLength(food, nFood);
    setLength(ejected, nEjected);
    this.finishEatEvents(nEat, ownLive);
    if (dyingId !== 0 && !dyingSeen) this.dyingPlayerId = 0;

    this.compactOrder();
    const order = this.order;
    // Insertion sort: the order is nearly sorted from the last frame, so this is ~O(n).
    for (let j = 1; j < order.length; j++) {
      const cur = order[j] as Rec;
      let k = j - 1;
      while (k >= 0 && drawsAfter(order[k] as Rec, cur)) {
        order[k + 1] = order[k] as Rec;
        k--;
      }
      order[k + 1] = cur;
    }
    const ownId = this.own.playerId;
    const ownView = this.dyingPlayerId;
    const bodies = v.bodies;
    const own = v.own;
    let nBodies = 0;
    let nOwn = 0;
    for (let j = 0; j < order.length; j++) {
      const rec = order[j] as Rec;
      if (!rec.visible) continue;
      bodies[nBodies++] = rec;
      if (
        rec.kind === KIND.CELL &&
        rec.playerId !== 0 &&
        (rec.playerId === ownId || rec.playerId === ownView)
      ) {
        own[nOwn++] = rec;
      }
    }
    setLength(bodies, nBodies);
    setLength(own, nOwn);
    this.releaseFreed();
    return v;
  }

  /** Ids known as of the last applied FRAME (ADDed, not yet EATEN/REMOVEd): the property test's set. */
  *liveIds(): IterableIterator<number> {
    for (const rec of this.list) if (rec.removedTick < 0) yield rec.id;
  }

  // -------------------------------------------------------------------------------------------
  // Getters for the renderer, HUD, audio and input (DOM-free)
  // -------------------------------------------------------------------------------------------

  /** The current socket's room is a money room (WELCOME money block present). */
  get isMoneyRoom(): boolean {
    return this.money.room !== null;
  }

  /** A player's value in lamports (FRAME MONEY), or null when unknown or in a free room. */
  playerValue(playerId: number): number | null {
    if (this.money.room === null) return null;
    return this.money.values.get(playerId) ?? null;
  }

  /** A practice room (WELCOME money block with `practice`, money-mode §1.4): play money. */
  get isPracticeRoom(): boolean {
    return this.money.room?.practice === true;
  }

  /** The current life's value in lamports, or null (not alive, free room, not sent yet). */
  get ownValue(): number | null {
    const id = this.own.playerId;
    return id === 0 ? null : this.playerValue(id);
  }

  /**
   * A player's cash-out ring 0–1 at local time `nowMs`: the last CASH_OUT_STATE progress plus
   * the time since it arrived over WELCOME `cashOutHoldSec` (protocol §5.2: clients animate
   * locally), capped at 1. Null when that player is not holding.
   */
  cashOutProgress(playerId: number, nowMs: number): number | null {
    const hold = this.money.holds.get(playerId);
    if (!hold) return null;
    const holdSec = this.money.room?.cashOutHoldSec ?? 0;
    if (!(holdSec > 0)) return hold.progress;
    const t = hold.progress + Math.max(0, nowMs - hold.atMs) / (holdSec * 1000);
    return t >= 1 ? 1 : t;
  }

  /** The own life's cash-out ring 0–1, or null when not holding (see `cashOutProgress`). */
  ownCashOutProgress(nowMs: number): number | null {
    const id = this.own.playerId;
    return id === 0 ? null : this.cashOutProgress(id, nowMs);
  }

  /** Own leaderboard rank (1-based) from the last LEADERBOARD; 0 = not ranked / not playing. */
  get ownRank(): number {
    return this.leaderboard.value?.yourRank ?? 0;
  }

  /**
   * Own leaderboard amount from the last LEADERBOARD amounts trailer (floor(score) in free rooms,
   * lamports in money rooms; see `leaderboardAmountKind`), also outside the top 10. 0 when not
   * playing; null when the board had no amounts trailer.
   */
  get ownAmount(): number | null {
    return this.leaderboard.value?.yourAmount ?? null;
  }

  /** What the leaderboard amounts count, or null when the board carries none. */
  get leaderboardAmountKind(): AmountKind | null {
    return this.leaderboard.value?.amountKind ?? null;
  }

  /**
   * The own cells in the newest FRAME (movement.md §4.2–4.3): r²-weighted centroid and velocity,
   * largest radius/mass and the count. Null without live own cells. Returns the same object on
   * every call (no allocation); copy what you keep.
   */
  ownNewest(): Readonly<OwnNewest> | null {
    const out = this.ownNewestOut;
    let w = 0;
    let sx = 0;
    let sy = 0;
    let svx = 0;
    let svy = 0;
    let rMax = 0;
    let count = 0;
    let tick = 0;
    const tickSec = this.dtMs / 1000;
    for (const id of this.own.cellIds) {
      const rec = this.entities.get(id) as Rec | undefined;
      if (!rec || rec.removedTick >= 0) continue;
      const h = rec.hist;
      const n = h.newest();
      if (!n) continue;
      const r2 = n.r * n.r;
      w += r2;
      sx += n.x * r2;
      sy += n.y * r2;
      const p = h.length >= 2 ? h.get(h.length - 2) : null;
      if (p && n.tick > p.tick && tickSec > 0) {
        const dt = (n.tick - p.tick) * tickSec;
        svx += ((n.x - p.x) / dt) * r2;
        svy += ((n.y - p.y) / dt) * r2;
      }
      if (n.r > rMax) rMax = n.r;
      if (n.tick > tick) tick = n.tick;
      count++;
    }
    if (count === 0 || !(w > 0)) return null;
    out.x = sx / w;
    out.y = sy / w;
    out.vx = svx / w;
    out.vy = svy / w;
    out.rMax = rMax;
    out.massMax = massOf(rMax);
    out.count = count;
    out.tick = tick;
    return out;
  }

  /** Latest (newest-sample) position of a live entity, for tests and the minimap; false if unknown. */
  latest(id: number, out: Point3): boolean {
    const rec = this.entities.get(id) as Rec | undefined;
    if (!rec || rec.removedTick >= 0) return false;
    const n = rec.hist.newest();
    if (!n) return false;
    out.x = n.x;
    out.y = n.y;
    out.r = n.r;
    return true;
  }

  // -------------------------------------------------------------------------------------------
  // FRAME sections
  // -------------------------------------------------------------------------------------------

  private applyPlayers(records: readonly PlayerRecord[]): void {
    for (const p of records) {
      const prev = this.players.get(p.playerId);
      // Delete first so the Map's insertion order stays "least recently seen first".
      if (prev) this.players.delete(p.playerId);
      this.players.set(p.playerId, p);
      if ((prev !== undefined && prev.color !== p.color) || (!prev && this.orphanCells > 0)) {
        this.recolorCells(p.playerId, p.color);
      }
    }
    if (this.players.size > MAX_PLAYER_RECORDS) this.prunePlayers();
  }

  private applyAdd(a: AddRecord, T: number): void {
    let rec = this.entities.get(a.id) as Rec | undefined;
    if (rec && rec.kind !== a.kind) {
      // Ids are never reused, so this is a protocol violation: start over.
      this.drop(rec);
      rec = undefined;
    }
    if (!rec) {
      rec = this.alloc();
      rec.id = a.id;
      rec.kind = a.kind;
      rec.slot = this.list.length;
      this.list.push(rec);
      this.entities.set(a.id, rec);
      if (isBodyKind(a.kind)) this.order.push(rec);
    } else if (rec.orphan) {
      // Re-ADD of a known id (it left the view and came back): recoloured below.
      rec.orphan = false;
      this.orphanCells--;
    }
    rec.playerId = a.kind === KIND.CELL ? a.playerId : 0;
    rec.color = this.colorFor(rec, a);
    rec.hist.reset(T, a.x, a.y, a.r);
    rec.addedTick = T;
    rec.removedTick = -1;
    rec.eaterId = 0;
    rec.eatReported = false;
    rec.x = a.x;
    rec.y = a.y;
    rec.r = a.r;
    rec.visible = false;
    rec.effect = ENTITY_EFFECT.NONE;
    rec.effectT = 0;
  }

  private colorFor(rec: Rec, a: AddRecord): number {
    switch (a.kind) {
      case KIND.CELL: {
        const p = this.players.get(a.playerId);
        if (!p) {
          rec.orphan = true;
          this.orphanCells++;
          return SPECIAL_COLORS.plain;
        }
        // Seen again: move to the "recently seen" end of the Map.
        this.players.delete(a.playerId);
        this.players.set(a.playerId, p);
        return p.color;
      }
      case KIND.FOOD:
        return pelletColor(a.id);
      case KIND.EJECTED:
        return a.color;
      case KIND.VIRUS:
        return SPECIAL_COLORS.virus;
      case KIND.SPAWNER:
        return SPECIAL_COLORS.spawner;
    }
  }

  private applyMove(m: MoveRecord, T: number): void {
    const rec = this.entities.get(m.id) as Rec | undefined;
    if (!rec || rec.removedTick >= 0) return;
    const n = rec.hist.newest();
    if (!n) return;
    let x = n.x;
    let y = n.y;
    let r = n.r;
    if ((m.mask & MOVE_MASK.ABS) !== 0) {
      x = m.x;
      y = m.y;
    } else if ((m.mask & MOVE_MASK.DELTA) !== 0) {
      // Relative to the last values sent for this entity (protocol §5.4), added in posQ steps so
      // repeated deltas never accumulate floating-point error.
      const q = this.posQ;
      x = (Math.round(n.x / q) + Math.round(m.x / q)) * q;
      y = (Math.round(n.y / q) + Math.round(m.y / q)) * q;
    }
    if ((m.mask & MOVE_MASK.RADIUS) !== 0) r = m.r;
    rec.hist.push(T, x, y, r);
  }

  private markRemoved(id: number, T: number, eaterId: number): void {
    const rec = this.entities.get(id) as Rec | undefined;
    if (!rec || rec.removedTick >= 0) return;
    rec.removedTick = T;
    rec.eaterId = eaterId;
    this.pendRec.push(rec);
    this.pendId.push(id);
    this.pendTick.push(T);
  }

  private applyMoney(m: MoneySection): void {
    const money = this.money;
    if (m.solUsd > 0 && Number.isFinite(m.solUsd)) money.solUsd = m.solUsd;
    for (const v of m.values) money.values.set(v.playerId, v.value);
    money.version++;
  }

  private applyOwn(own: OwnSection): void {
    this.own.score = own.score;
    const ids = this.own.cellIds;
    ids.clear();
    for (const id of own.cellIds) ids.add(id);
    if (this.own.playerId !== 0) this.own.alive = true;
  }

  /** DEATH or CASH_OUT_DONE: the life's cells stay `view.own` until they leave the display. */
  private endOwnLife(): void {
    if (this.own.playerId !== 0) this.dyingPlayerId = this.own.playerId;
    this.own.playerId = 0;
    this.own.cellIds.clear();
    this.own.score = 0;
    this.own.alive = false;
  }

  // -------------------------------------------------------------------------------------------
  // sample() helpers
  // -------------------------------------------------------------------------------------------

  /** Fills eat event `n` from the eaten record (called before the record may be dropped). */
  private recordEat(rec: Rec, n: number, ownId: number, dyingId: number): void {
    const pool = this.eatPool;
    let ev = pool[n];
    if (!ev) {
      ev = newEatEvent();
      pool.push(ev);
    }
    const eater = this.entities.get(rec.eaterId) as Rec | undefined;
    ev.eatenId = rec.id;
    ev.eatenKind = rec.kind;
    ev.eatenPlayerId = rec.kind === KIND.CELL ? rec.playerId : 0;
    ev.eaterId = rec.eaterId;
    ev.eaterKind = eater ? eater.kind : null;
    ev.eaterPlayerId = eater && eater.kind === KIND.CELL ? eater.playerId : 0;
    ev.eatenOwn = isOwnCell(rec, ownId, dyingId);
    ev.eaterOwn = eater !== undefined && isOwnCell(eater, ownId, dyingId);
    ev.ownLeft = 0;
    ev.tick = rec.removedTick;
    this.view.eatEvents[n] = ev;
  }

  /** Sets the eat-event count and each event's `ownLeft` (own-cell removals count down). */
  private finishEatEvents(n: number, ownLive: number): void {
    const list = this.view.eatEvents;
    if (list.length !== n) list.length = n;
    let left = ownLive;
    for (let k = n - 1; k >= 0; k--) {
      const ev = list[k] as EatEventRec;
      ev.ownLeft = left;
      if (ev.eatenOwn) left++;
    }
  }

  /** Writes the eaten slide into `rec`; false when the effect is over (drop the record). */
  private sampleEaten(rec: Rec, rt: number, dtMs: number): boolean {
    const t = eatenProgress(rt, rec.removedTick, dtMs, INTERPOLATION.eatenEffectMs);
    if (t >= 1) return false;
    rec.hist.at(rt, rec);
    const eater = this.entities.get(rec.eaterId) as Rec | undefined;
    const tmp = this.tmp;
    if (eater && eater !== rec && eater.slot >= 0 && eater.hist.at(rt, tmp)) {
      rec.x += (tmp.x - rec.x) * t;
      rec.y += (tmp.y - rec.y) * t;
    }
    rec.r *= 1 - t;
    rec.visible = true;
    rec.effect = ENTITY_EFFECT.EATEN;
    rec.effectT = t;
    return true;
  }

  // -------------------------------------------------------------------------------------------
  // Record bookkeeping
  // -------------------------------------------------------------------------------------------

  private alloc(): Rec {
    return this.pool.pop() ?? new Rec();
  }

  /** Removes a record from the store at once (swap-remove from the dense list). */
  private drop(rec: Rec): void {
    const slot = rec.slot;
    if (slot < 0) return;
    const list = this.list;
    const last = list.pop() as Rec;
    if (last !== rec) {
      list[slot] = last;
      last.slot = slot;
    }
    rec.slot = -1;
    rec.visible = false;
    if (this.entities.get(rec.id) === rec) this.entities.delete(rec.id);
    if (rec.orphan) {
      rec.orphan = false;
      this.orphanCells--;
    }
    // Bodies stay in `order` until the next compaction, so the record must not be reused before.
    if (isBodyKind(rec.kind)) this.freed.push(rec);
    else this.release(rec);
  }

  private release(rec: Rec): void {
    rec.orphan = false;
    rec.eatReported = false;
    rec.hist.clear();
    if (this.pool.length < MAX_POOLED_RECORDS) this.pool.push(rec);
  }

  private compactOrder(): void {
    const order = this.order;
    let w = 0;
    for (let j = 0; j < order.length; j++) {
      const rec = order[j] as Rec;
      if (rec.slot >= 0) order[w++] = rec;
    }
    if (order.length !== w) order.length = w;
  }

  private releaseFreed(): void {
    const freed = this.freed;
    if (freed.length === 0) return;
    for (let j = 0; j < freed.length; j++) this.release(freed[j] as Rec);
    freed.length = 0;
  }

  /** Compacts the body order and recycles dropped bodies (outside `sample()`). */
  private reclaim(): void {
    this.compactOrder();
    this.releaseFreed();
  }

  /** Drops removed records whose removal is long past (no display can still need them). */
  private sweepStale(T: number): void {
    const limit = T - Math.ceil(STALE_REMOVAL_MS / this.dtMs);
    const recs = this.pendRec;
    while (this.pendHead < recs.length) {
      const h = this.pendHead;
      const tick = this.pendTick[h] as number;
      if (tick > limit) break;
      const rec = recs[h] as Rec;
      if (rec.slot >= 0 && rec.id === this.pendId[h] && rec.removedTick === tick) this.drop(rec);
      this.pendHead++;
    }
    if (this.pendHead === recs.length) {
      recs.length = 0;
      this.pendId.length = 0;
      this.pendTick.length = 0;
      this.pendHead = 0;
    } else if (this.pendHead >= 1024 && this.pendHead * 2 >= recs.length) {
      const h = this.pendHead;
      const n = recs.length - h;
      recs.copyWithin(0, h);
      this.pendId.copyWithin(0, h);
      this.pendTick.copyWithin(0, h);
      recs.length = n;
      this.pendId.length = n;
      this.pendTick.length = n;
      this.pendHead = 0;
    }
  }

  private recolorCells(playerId: number, color: number): void {
    for (const rec of this.list) {
      if (rec.kind !== KIND.CELL || rec.playerId !== playerId) continue;
      rec.color = color;
      if (rec.orphan) {
        rec.orphan = false;
        this.orphanCells--;
      }
    }
  }

  /**
   * Keeps the PLAYERS map bounded over a long session (the server never says when it forgets a
   * player): forgets the least recently seen players that have no cell in the store, down to half
   * the cap. Players seen recently, or with cells on screen, are never forgotten.
   */
  private prunePlayers(): void {
    const keep = new Set<number>();
    for (const rec of this.list) if (rec.kind === KIND.CELL) keep.add(rec.playerId);
    if (this.own.playerId !== 0) keep.add(this.own.playerId);
    if (this.dyingPlayerId !== 0) keep.add(this.dyingPlayerId);
    const target = MAX_PLAYER_RECORDS >> 1;
    for (const id of this.players.keys()) {
      if (this.players.size <= target) break;
      if (!keep.has(id)) {
        this.players.delete(id);
        this.money.values.delete(id);
      }
    }
  }
}

// ---------------------------------------------------------------------------------------------
// Money display helpers (money-mode §7). Display only: the client never computes a value.
// ---------------------------------------------------------------------------------------------

/**
 * The unit labels use: the setting (`settings.labelCurrency`) when it is USD or SOL, else ('auto')
 * the room's WELCOME default, else USD (money-mode §7: USD is the default everywhere).
 */
export function resolveLabelCurrency(
  setting: LabelCurrency | 'auto',
  room: Pick<WelcomeMoneyInfo, 'labelCurrency'> | null,
): LabelCurrency {
  if (setting === 'USD' || setting === 'SOL') return setting;
  return room?.labelCurrency ?? 'USD';
}

/**
 * A lamport amount as a label: USD `"$0.90"` (two decimals) at `solUsd`, or SOL `"0.0060 SOL"`
 * (4 decimals below 0.01 SOL, 3 below 1, else 2). USD with an unknown price (`solUsd` ≤ 0)
 * falls back to SOL. Non-finite or negative amounts format as zero.
 */
export function formatMoney(lamports: number, currency: LabelCurrency, solUsd: number): string {
  const v = Number.isFinite(lamports) && lamports > 0 ? lamports : 0;
  if (currency === 'USD' && solUsd > 0 && Number.isFinite(solUsd)) {
    return `$${lamportsToUsd(v, solUsd).toFixed(2)}`;
  }
  const sol = lamportsToSol(v);
  const digits = sol >= 1 ? 2 : sol >= 0.01 ? 3 : 4;
  return `${sol.toFixed(digits)} SOL`;
}

/**
 * A leaderboard amount for display (owner exception (a): live points next to each name): free
 * rooms (AMOUNT_KIND.SCORE) → the integer score, money rooms (LAMPORTS) → `formatMoney`.
 */
export function formatLeaderboardAmount(
  amount: number,
  kind: AmountKind,
  currency: LabelCurrency,
  solUsd: number,
): string {
  if (kind === AMOUNT_KIND.LAMPORTS) return formatMoney(amount, currency, solUsd);
  return String(Math.max(0, Math.floor(Number.isFinite(amount) ? amount : 0)));
}
